import {
  Component,
  HostListener,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Injector,
  OnInit,
} from "@angular/core";
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  NgControl,
} from "@angular/forms";
import { ListItem, IDropdownSettings } from "./select.model";
import {
  BroadcastService,
  AppEvent,
  AppEventType,
} from "@app/shared/services/broadcast.service";

export const DROPDOWN_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SelectComponent),
  multi: true,
};

const noop = () => {};

@Component({
  selector: "db-select",
  templateUrl: "./select.component.html",
  styleUrls: ["./select.component.scss"],
  providers: [DROPDOWN_CONTROL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent implements ControlValueAccessor, OnInit {
  public _settings: IDropdownSettings;
  public _data: Array<ListItem> = [];
  private _rawData: any[] = [];
  public selectedItems: Array<ListItem> = [];
  public isDropdownOpen = true;

  ngControl: NgControl;

  labelValue = "Select";
  _placeholder = "Select";
  filter: ListItem = new ListItem(this.data);
  defaultSettings: IDropdownSettings = {
    allowSearchFilter: false,
    clearSearchFilter: true,
    closeDropDownOnSelection: true,
    defaultOpen: false,
    enableCheckAll: true,
    idField: "id",
    itemsShowLimit: 999,
    limitSelection: -1,
    maxHeight: 197,
    noDataAvailablePlaceholderText: "No data available",
    searchPlaceholderText: "Search",
    selectAllText: "Select All",
    showSelectedItemsAtTop: false,
    singleSelection: true,
    textField: "text",
    unSelectAllText: "UnSelect All",
  };

  @Input() showLabel = true;

  @Input() public set label(value: string) {
    if (value) {
      this.labelValue = value;
    } else {
      this.labelValue = "Select";
    }
  }

  @Input() public set placeholder(value: string) {
    if (value) {
      this._placeholder = value;
    } else {
      this._placeholder = "Select";
    }
  }

  @Input() disabled = false;

  @Input() public set settings(value: IDropdownSettings) {
    const base = { ...this.defaultSettings };
    if (value && typeof value === 'object') {
      this._settings = { ...base, ...value };
    } else {
      this._settings = base;
    }

    if (this._rawData?.length) {
      this.remapData();
      this.cdr.markForCheck();
    }
  }

  @Input() public set data(value: Array<any>) {
    if (!value) {
      this._data = [];
      return;
    }
    this._rawData = value;

    if (!this._settings) {
      this._settings = { ...this.defaultSettings };
    }
    this.remapData();
    this.cdr.markForCheck();
  }

  @Output("filterChange") filterChange: EventEmitter<ListItem> =
    new EventEmitter<any>();
  @Output("dropdownClose") dropdownClose: EventEmitter<ListItem> =
    new EventEmitter<any>();
  @Output("select") select: EventEmitter<ListItem> = new EventEmitter<any>();
  @Output("deSelect") deSelect: EventEmitter<ListItem> =
    new EventEmitter<any>();
  @Output("selectAll") selectAll: EventEmitter<Array<ListItem>> =
    new EventEmitter<Array<any>>();
  @Output("deSelectAll") deSelectAll: EventEmitter<Array<ListItem>> =
    new EventEmitter<Array<any>>();

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  onFilterTextChange($event) {
    this.filterChange.emit($event);
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private _broadcastService: BroadcastService,
    private injector: Injector
  ) {}

  ngOnInit() {
    this.ngControl = this.injector.get(NgControl);
  }

  onItemClick($event: any, item: ListItem) {
    if (this.disabled) {
      return false;
    }
    this._broadcastService.dispatch(
      new AppEvent(AppEventType.DataFetchingStarted)
    );
    const found = this.isSelected(item);
    const allowAdd =
      this._settings.limitSelection === -1 ||
      (this._settings.limitSelection > 0 &&
        this.selectedItems.length < this._settings.limitSelection);
    if (!found) {
      if (allowAdd) {
        this.addSelected(item);
      }
    } else {
      this.removeSelected(item);
    }
    if (
      this._settings.singleSelection &&
      this._settings.closeDropDownOnSelection
    ) {
      this.closeDropdown();
    }
  }

  writeValue(value: any) {
    if (value !== undefined && value !== null && value.length > 0) {
      if (this._settings.singleSelection) {
        try {
          if (value.length >= 1) {
            const firstItem = value[0];
            this.selectedItems = [
              typeof firstItem === "string"
                ? new ListItem(firstItem)
                : new ListItem({
                    id: firstItem[this._settings.idField],
                    text: firstItem[this._settings.textField],
                  }),
            ];
          }
        } catch (e) {
        }
      } else {
        const _data = value.map((item: any) =>
          typeof item === "string"
            ? new ListItem(item)
            : new ListItem({
                id: item[this._settings.idField],
                text: item[this._settings.textField],
              })
        );
        if (this._settings.limitSelection > 0) {
          this.selectedItems = _data.splice(0, this._settings.limitSelection);
        } else {
          this.selectedItems = _data;
        }
      }
    } else {
      this.selectedItems = [];
    }
    this.onChangeCallback(value);
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  @HostListener("blur") public onBlur() {
    this.closeDropdown();
    this.onTouchedCallback();
  }

  @HostListener("click") public onTouched() {
    this.onTouchedCallback();
  }

  trackByFn(index, item) {
    return item.id;
  }

  isSelected(clickedItem: ListItem) {
    let found = false;
    this.selectedItems.forEach((item) => {
      if (clickedItem.id === item.id) {
        found = true;
      }
    });
    return found;
  }

  isLimitSelectionReached(): boolean {
    return this._settings.limitSelection === this.selectedItems.length;
  }

  isAllItemsSelected(): boolean {
    return this._data.length === this.selectedItems.length;
  }

  showButton(): boolean {
    if (!this._settings.singleSelection) {
      if (this._settings.limitSelection > 0) {
        return false;
      }

      return true;
    } else {
      return false;
    }
  }

  itemShowRemaining(): number {
    return this.selectedItems.length - this._settings.itemsShowLimit;
  }

  addSelected(item: ListItem) {
    if (this._settings.singleSelection) {
      this.selectedItems = [];
      this.selectedItems.push(item);
    } else {
      this.selectedItems.push(item);
    }
    this.onChangeCallback(this.emittedValue(this.selectedItems));
    this.select.emit(this.emittedValue(item));
  }

  removeSelected(itemSel: ListItem) {
    this.selectedItems.forEach((item) => {
      if (itemSel.id === item.id) {
        this.selectedItems.splice(this.selectedItems.indexOf(item), 1);
      }
    });
    this.onChangeCallback(this.emittedValue(this.selectedItems));
    this.deSelect.emit(this.emittedValue(itemSel));
  }

  emittedValue(val: any): any {
    const selected = [];
    if (Array.isArray(val)) {
      val.map((item) => {
        if (item.id === item.text) {
          selected.push(item.text);
        } else {
          selected.push(this.objectify(item));
        }
      });
    } else {
      if (val) {
        if (val.id === val.text) {
          return val.text;
        } else {
          return this.objectify(val);
        }
      }
    }
    return selected;
  }

  objectify(val: ListItem) {
    if (!this._settings) {
      this._settings = { ...this.defaultSettings };
    }
    const obj = {};
    const idKey = this._settings.idField || 'id';
    const textKey = this._settings.textField || 'text';
    obj[idKey] = val.id;
    obj[textKey] = val.text;
    return obj;
  }

  private remapData() {
    const idKey = this._settings?.idField || 'id';
    const textKey = this._settings?.textField || 'text';
    this._data = (this._rawData || []).map((item: any) => {
      if (typeof item === 'string') {
        return new ListItem(item);
      }
      const safeId = item?.[idKey];
      const safeText = item?.[textKey];
      return new ListItem({ id: safeId, text: safeText });
    });
  }

  toggleDropdown(evt) {
    evt.preventDefault();
    if (this.disabled && this._settings.singleSelection) {
      return;
    }
    this._settings.defaultOpen = !this._settings.defaultOpen;
    if (!this._settings.defaultOpen) {
      this.dropdownClose.emit();
    }
  }

  closeDropdown() {
    this._settings.defaultOpen = false;

    if (this._settings.clearSearchFilter) {
      this.filter.text = "";
    }
    this.dropdownClose.emit();
  }

  toggleSelectAll() {
    if (this.disabled) {
      return false;
    }
    this._broadcastService.dispatch(
      new AppEvent(AppEventType.DataFetchingStarted)
    );
    if (!this.isAllItemsSelected()) {
      this.selectedItems = this._data.slice();
      this.selectAll.emit(this.emittedValue(this.selectedItems));
    } else {
      this.selectedItems = [];
      this.deSelectAll.emit(this.emittedValue(this.selectedItems));
    }
    this.onChangeCallback(this.emittedValue(this.selectedItems));
  }
}
